import React from "react";
import { CustomComponentSlot as BaseCustomComponentSlot } from "@shopflo/ui/custom-component-slot";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { CheckoutMainPages, CheckoutSectionType } from "lib/types/checkout";
import { CartSectionType } from "lib/types/cart";
import { useIsSubscribeMode } from "lib/hooks/useIsSubscribeMode";

type SlotPosition = CheckoutSectionType | CartSectionType;

// Resolves which merchant component belongs in this checkout/cart slot. Rendering
// and script execution live in the shared @shopflo/ui slot.
const CustomComponentSlot: React.FC<{ position: SlotPosition; type?: CheckoutMainPages }> = ({
  position,
  type,
}) => {
  const {
    state: { checkoutView, rawCheckoutObject },
  } = useCheckoutContext();
  const {
    state: { checkoutMetadata, cartMetadata, ssoMetadata },
  } = useMerchantContext();

  const isSubscribeMode = useIsSubscribeMode();

  const isHiddenInSubscribe =
    isSubscribeMode &&
    (["COUPONS_SECTION", "REWARDS_SECTION", "UPSELL_SECTION", "ADD_ON_SECTION"] as SlotPosition[]).includes(
      position,
    );

  const resolvedType = type ?? (checkoutView as CheckoutMainPages);

  // Only one context's metadata is loaded per page, so at most one of these matches.
  const html =
    (
      checkoutMetadata?.uiAttributes?.customComponentsConfig?.components?.find(
        (c) => c.position === position && c.type === resolvedType && c.enabled,
      ) ??
      cartMetadata?.uiAttributes?.customComponentsConfig?.components?.find(
        (c) => c.position === position && c.enabled,
      )
    )?.htmlTextString ??
    // SSO metadata is not parsed, so its components stay snake_case.
    ssoMetadata?.custom_component_config?.custom_components?.find(
      (c) => c?.position === position && c?.enabled,
    )?.html_text_string;

  return (
    <BaseCustomComponentSlot
      html={html !== undefined && !isHiddenInSubscribe ? html : null}
      // Re-run merchant scripts on every checkout update so they read fresh window.Shopflo state.
      rerunDeps={[rawCheckoutObject, isHiddenInSubscribe]}
    />
  );
};

export default CustomComponentSlot;
